import { useEffect, useRef } from "react";

/**
 * Load-phase tracing for debugging slow / flickering initial loads.
 *
 * Every message is prefixed with ms since navigation start, so the console
 * reads as a timeline. Turn the whole thing off in one place by setting
 * REACT_APP_PERF_LOG=off (or flipping ENABLED below) — no need to hunt
 * down individual calls.
 */
const ENABLED = process.env.REACT_APP_PERF_LOG !== "off";

const CHANNEL_COLOR = {
  boot: "#7c3aed",
  net: "#0ea5e9",
  paint: "#16a34a",
  gate: "#f59e0b",
  blank: "#dc2626",
};

const now = () => Math.round(performance.now());

export function plog(channel, label, detail) {
  if (!ENABLED) return;
  const color = CHANNEL_COLOR[channel] || "#6b7280";
  const prefix = `%c${String(now()).padStart(5)}ms %c${channel.toUpperCase().padEnd(5)}%c ${label}`;
  const styles = [
    "color:#6b7280",
    `color:${color};font-weight:bold`,
    "color:inherit",
  ];
  if (detail === undefined) console.log(prefix, ...styles);
  else console.log(prefix, ...styles, detail);
}

/**
 * Logs when a section is mounted-but-empty vs actually showing content.
 * The window between those two is exactly the blank-screen/flicker window.
 */
export function useSectionLog(name, ready) {
  const prev = useRef("init");

  useEffect(() => {
    plog("boot", `${name}: mounted`);
    return () => plog("gate", `${name}: unmounted`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const state = ready ? "CONTENT" : "blank (returns null)";
    if (prev.current !== state) {
      plog(ready ? "paint" : "blank", `${name}: ${state}`);
      prev.current = state;
    }
  }, [name, ready]);
}

/**
 * One-shot summary of the load, printed once the page has settled, so the
 * phase breakdown doesn't have to be reconstructed by eye from the timeline.
 */
export function installLoadSummary() {
  if (!ENABLED || typeof window === "undefined") return;
  if (window.__perfSummaryInstalled) return;
  window.__perfSummaryInstalled = true;

  const report = () => {
    const res = performance.getEntriesByType("resource");
    const end = (list) =>
      list.length ? Math.round(Math.max(...list.map((r) => r.responseEnd))) : null;

    const assets = res.filter(
      (r) => r.name.endsWith(".js") || r.name.endsWith(".css")
    );
    const apiCalls = res.filter((r) => r.name.includes("/api/"));
    const paints = performance.getEntriesByType("paint");
    const fcp = paints.find((p) => p.name === "first-contentful-paint");

    const apiStart = apiCalls.length
      ? Math.round(Math.min(...apiCalls.map((r) => r.startTime)))
      : null;

    console.groupCollapsed(
      "%c⏱ LOAD SUMMARY (expand)",
      "color:#7c3aed;font-weight:bold"
    );
    console.table({
      "JS/CSS downloaded": { ms: end(assets) },
      "first API request sent": { ms: apiStart },
      "last API response": { ms: end(apiCalls) },
      "first contentful paint": { ms: fcp ? Math.round(fcp.startTime) : "n/a" },
      "API request count": { ms: apiCalls.length },
    });
    if (apiStart != null && end(assets) != null) {
      plog(
        "gate",
        `dead time between assets ready and first fetch: ${apiStart - end(assets)}ms`
      );
    }
    console.groupEnd();
  };

  if (document.readyState === "complete") setTimeout(report, 2500);
  else window.addEventListener("load", () => setTimeout(report, 2500));
}
